<template>
  <StepComponent>
    <v-row>
      <v-col cols="12" sm="6">
        <PhoneInput
          v-model="workPhone"
          :countryId.sync="workPhoneCountryId"
          label="Work Phone"
          :rules="rules.phone"
        />
      </v-col>

      <v-col cols="12" sm="6">
        <PhoneInput
          v-model="homePhone"
          :countryId.sync="homePhoneCountryId"
          label="Home Phone"
          :rules="rules.phone"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" sm="6">
        <PhoneInput
          v-model="mobilePhone"
          :countryId.sync="mobilePhoneCountryId"
          label="Mobile Phone"
          :rules="rules.phone"
        />
      </v-col>

      <v-col cols="12" sm="6">
        <PhoneInput
          v-model="faxNumber"
          :countryId.sync="faxCountryId"
          label="Fax Number"
          :rules="rules.fax"
        />
      </v-col>
    </v-row>
    <v-row
      v-for="(email, index) in client.clients_email_addresses"
      :key="index"
    >
      <v-col :cols="index === 0 ? 11 : 6">
        <v-text-field
          type="email"
          ref="emailFields"
          v-model="email.email_address"
          @blur="email.email_address = email.email_address?.trim()"
          :id="`email_${index}`"
          :rules="[...rules.email, uniqueEmailRule(index)]"
          :label="emailLabel(index)"
        ></v-text-field>
      </v-col>
      <v-row class="mt-6 d-flex">
        <v-col cols="1" v-if="index > 0">
          <v-icon id="deleteEmail" @click="deleteEmail(index)"
            >mdi-minus</v-icon
          >
        </v-col>
        <v-col cols="1">
          <v-icon id="addEmail" @click="addEmail">mdi-plus</v-icon>
        </v-col>
      </v-row>
    </v-row>
  </StepComponent>
</template>

<script>
import StepComponent from "@/components/registration/AccountSetupSteps/StepComponent";
import { mapActions, mapMutations, mapGetters } from "vuex";
import { mapFields, mapMultiRowFields } from "vuex-map-fields";
import rules from "@/utils/rules";
// import VueTelInputVuetify from "vue-tel-input-vuetify/lib/vue-tel-input-vuetify.vue";
import "vuetify/dist/vuetify.min.css";
import PhoneInput from "@/components/shared/PhoneInput";

export default {
  name: "ContactInformationStep",
  components: { StepComponent, PhoneInput },
  data() {
    return {
      rules: {
        email: [rules.required, rules.email],
        required: [rules.required],
        phone: [
          number =>
            /^\s*[-()0-9+ ]+\s*$/.test(number) ||
            number === "" ||
            "Invalid phone number (digits only allowed). Keep adding digits to remove invalid characters",
          rules.required
        ],
        fax: [
          number =>
            /^\s*[-()0-9+ ]+\s*$/.test(number) ||
            number === "" ||
            "Invalid fax number (digits only allowed). Keep adding digits to remove invalid characters",
          rules.required
        ]
      },
      bindProps: {
        mode: "international",
        disabledFetchingCountry: true,
        defaultCountry: "US"
      },
      isMounted: false,
      dialCodes: {
        // workPhone: "+1",
        // homePhone: "+1",
        // mobilePhone: "+1",
        // faxNumber: "+1"
      },
      workPhoneCountryId: null,
      homePhoneCountryId: null,
      mobilePhoneCountryId: null,
      faxCountryId: null
    };
  },
  computed: {
    ...mapGetters("clients", ["selectedClient"]),
    workPhone: {
      get() {
        return this.client.work_phone || "";
      },
      set(val) {
        this.client.work_phone = val;
      }
    },
    homePhone: {
      get() {
        return this.client.home_phone || "";
      },
      set(val) {
        this.client.home_phone = val;
      }
    },
    mobilePhone: {
      get() {
        return this.client.mobile_phone || "";
      },
      set(val) {
        this.client.mobile_phone = val;
      }
    },
    faxNumber: {
      get() {
        return this.client.fax || "";
      },
      set(val) {
        this.client.fax = val;
      }
    },
    ...mapFields("clients", [
      "items[0].work_phone",
      "items[0].home_phone",
      "items[0].mobile_phone",
      "items[0].fax",
      "client"
    ]),
    ...mapMultiRowFields("clients", ["items[0].clients_email_addresses"])
  },
  methods: {
    ...mapMutations("clients", ["addEmail", "deleteEmail"]),
    ...mapMutations("clients", { workPhoneMutation: "updateWorkPhone" }),
    ...mapActions("clients", ["getClients", "updateClientContactInfo"]),
    emailLabel(idx = 0) {
      return idx === 0 ? "Primary Email" : "Email";
    },
    beforeContinue() {
      return this.updateClientContactInfo().catch(e => {
        console.error(e);
        return false;
      });
    },
    uniqueEmailRule(currentIndex) {
      return value => {
        if (!value) return true;

        const emails = this.client.clients_email_addresses
          .map((e, i) =>
            i !== currentIndex ? e.email_address?.toLowerCase() : null
          )
          .filter(Boolean);

        return (
          !emails.includes(value.toLowerCase()) ||
          "Email addresses must be unique"
        );
      };
    },
    enforceDialCode(field, country) {
      const dialCode =
        country && country.dialCode && country.dialCode.length
          ? `+${country.dialCode}`
          : "+";

      if (!this[field] || this[field].length === 0) {
        this[field] = dialCode;
        return;
      }

      if (!this[field].startsWith(dialCode)) {
        let digits = this[field].replace(/\D/g, "");
        digits = digits.replace(new RegExp(`^${dialCode}`), "");
        this[field] = dialCode + digits;
      }
    },
    normalizePhoneWithDialCode(field, newValue) {
      const dialCode = this.dialCodes[field] || "+";

      if (!newValue) {
        this[field] = dialCode;
        return;
      }

      if (!newValue.startsWith(dialCode)) {
        this.$nextTick(() => {
          this[field] = dialCode;
        });
        return;
      }

      if (newValue === "+" || newValue.length < dialCode.length) {
        this.$nextTick(() => {
          this[field] = dialCode;
        });
        return;
      }

      // Extract only the part AFTER dial code
      const rest = newValue.slice(dialCode.length);

      // Keep only digits in the rest
      const cleanRest = rest.replace(/\D/g, "");

      this[field] = dialCode + cleanRest;
    },
    onPhoneCountryChange(field, country) {
      const dialCode = `+${country.dialCode}`;
      this.dialCodes[field] = dialCode;

      this.$nextTick(() => {
        this[field] = dialCode;
      });
    },
    validateAllEmails() {
      const refs = this.$refs.emailFields;
      if (!refs) return;

      const fields = Array.isArray(refs) ? refs : [refs];

      fields.forEach(field => {
        field?.validate?.();
      });
    },
    handlePhoneKeydown(field, event) {
      const dialCode = this.dialCodes[field] || "+";
      const input = event.target;

      const cursorStart = input.selectionStart;

      if (cursorStart < dialCode.length) {
        if (
          event.key === "Backspace" ||
          event.key === "Delete" ||
          (event.key.length === 1 && !event.ctrlKey)
        ) {
          event.preventDefault();
        }
      }
    },
    fixCursorPosition(field, event) {
      const dialCode = this.dialCodes[field] || "+";
      const input = event.target;

      this.$nextTick(() => {
        if (input.selectionStart < dialCode.length) {
          input.setSelectionRange(dialCode.length, dialCode.length);
        }
      });
    },
    initCountryFromPhone(phoneField, countryField) {
      const val = this[phoneField];
      if (!val) return;

      const match = val.match(/^\+(\d+)/);
      if (!match) return;

      const dial = match[1];

      const found = this.$store.getters["baseData/countries"].find(
        c => this.dialCodes[c.country_code] === dial
      );

      if (found) {
        this[countryField] = found.id;
      }
    }
  },
  mounted() {
    this.client = this.selectedClient;
    this.initCountryFromPhone("workPhone", "workPhoneCountryId");
    this.initCountryFromPhone("homePhone", "homePhoneCountryId");
    this.initCountryFromPhone("mobilePhone", "mobilePhoneCountryId");
    this.initCountryFromPhone("faxNumber", "faxCountryId");

    Promise.all([this.getClients()]).then(() => {
      if (this.client.clients_email_addresses.length === 0) {
        this.addEmail();
        this.client.clients_email_addresses[0]["email_address"] =
          this.$store.state.auth.user.email ||
          this.$store.state.auth.user.username;
      }
    });
    setTimeout(() => {
      this.isMounted = true;
    }, 500);
  },
  watch: {
    selectedClient(newVal) {
      this.client = newVal;
      // this.normalizePhoneWithDialCode("workPhone", this.workPhone);
      // this.normalizePhoneWithDialCode("homePhone", this.homePhone);
      // this.normalizePhoneWithDialCode("mobilePhone", this.mobilePhone);
      // this.normalizePhoneWithDialCode("faxNumber", this.faxNumber);
    },
    "client.clients_email_addresses": {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.validateAllEmails();
        });
      }
    }
    // workPhone(newValue) {
    //   this.normalizePhoneWithDialCode("workPhone", newValue);
    // },
    // homePhone(newValue) {
    //   this.normalizePhoneWithDialCode("homePhone", newValue);
    // },
    // mobilePhone(newValue) {
    //   this.normalizePhoneWithDialCode("mobilePhone", newValue);
    // },
    // faxNumber(newValue) {
    //   this.normalizePhoneWithDialCode("faxNumber", newValue);
    // }
  }
};
</script>
